import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, notification, Spin, Alert, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  getWizardById,
  updateWizard,
} from "../../../services/wizardCmsService";
import { getPublicWizardUrl } from "../utils/url";
import { Wizard } from "../types";
import WizardForm from "../components/WizardForm";

const { Title } = Typography;

const EditWizardPage: React.FC = () => {
  const { wizardId } = useParams<{ wizardId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isFormDirty, setIsFormDirty] = useState(false);

  const {
    data: wizard,
    isLoading,
    isError,
  } = useQuery<Wizard, Error>({
    queryKey: ["wizard", wizardId],
    queryFn: () => getWizardById(wizardId!),
    enabled: !!wizardId,
  });

  const { mutate, status } = useMutation<
    Wizard,
    Error,
    Pick<Wizard, "name" | "content">
  >({
    mutationFn: (wizardData) => updateWizard(wizardId!, wizardData),
    onSuccess: (data) => {
      notification.success({
        message: "Wizard Updated",
        description: (
          <span>
            The wizard has been saved successfully. View it at:{" "}
            <a
              href={getPublicWizardUrl(data.wizardId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getPublicWizardUrl(data.wizardId)}
            </a>
          </span>
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["wizards"] });
      queryClient.invalidateQueries({
        queryKey: ["wizard", wizardId],
      });
      setIsFormDirty(false);
    },
    onError: (error: Error) => {
      notification.error({
        message: "Update Failed",
        description: error.message || "There was an error saving the wizard.",
      });
    },
  });

  if (isLoading) {
    return (
      <Spin size="large" style={{ display: "block", marginTop: "50px" }} />
    );
  }

  if (isError || !wizard) {
    return <div>Error loading wizard data.</div>;
  }

  return (
    <div style={{ padding: "24px" }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/wizards")}
        style={{ marginBottom: 16 }}
      >
        Back to Wizards
      </Button>
      <Title level={2} style={{ marginBottom: 24 }}>
        Edit Wizard: <span style={{ fontWeight: "normal" }}>{wizard.name}</span>
      </Title>
      <WizardForm
        wizard={wizard}
        onFinish={(wizardData) => {
          const { name, content } = wizardData;
          mutate({ name, content });
        }}
        isLoading={status === "pending"}
        onValuesChange={() => setIsFormDirty(true)}
        isSaveDisabled={!isFormDirty}
      />
    </div>
  );
};

export default EditWizardPage;
